import {
  ReactElement,
  SyntheticEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { Stack, FormHelperText, Tooltip, IconButton } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useFormikContext, FieldArray, ArrayHelpers } from 'formik';
import { find, get, indexOf, includes } from 'lodash';
import { DndProvider, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { Nullable, SelectOption } from '~globals/types';
import { getHelperText } from '~utils/formHelpers';
import CustomAutocomplete from '~components/CustomAutocomplete';

import RoadmapColumnsFieldSortableItem from './SortableItem';
import { RoadmapColumnsFieldProps } from './types';
import { ITEM_TYPES_DND, getInputsMaxWidthPlusIcon } from './utils';

const RoadmapColumnsField = ({
  name,
  options,
  disabled = false,
  inputsMaxWidth = 250,
}: RoadmapColumnsFieldProps): ReactElement => {
  const { values, errors, touched, setFieldTouched } = useFormikContext<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: string;
  }>();

  const selectedColumnsList = useMemo(
    () => values[name] as unknown as string[],
    [values, name],
  );

  const findCurrentListColumnIndex = useCallback(
    (columnId: string) => indexOf(selectedColumnsList, columnId),
    [selectedColumnsList],
  );

  const getSelectedColumnLabel = useCallback(
    (columnId: string) => {
      const currentColumnData = find(options, { value: columnId });

      return get(currentColumnData, 'label', '').toString();
    },
    [options],
  );

  const getAutocompleteOptionDisabled = useCallback(
    (currentOpton: SelectOption) =>
      includes(selectedColumnsList, currentOpton.value.toString()),
    [selectedColumnsList],
  );

  const error = useMemo(
    () => getHelperText(touched, errors, name),
    [errors, name, touched],
  );

  const [newColumn, setNewColumn] = useState<Nullable<SelectOption>>(null);

  const handleChangeNewItem = useCallback(
    (_event: SyntheticEvent, value: Nullable<SelectOption>) => {
      setNewColumn(value);
    },
    [],
  );

  const handleAddColumInList = useCallback(
    (insertHelper: ArrayHelpers['insert']) => {
      if (newColumn) {
        const insertPositionIndex = selectedColumnsList.length;

        insertHelper(insertPositionIndex, newColumn.value);

        setNewColumn(null);
      }
    },
    [newColumn, selectedColumnsList],
  );

  const handleMoveColumInList = useCallback(
    (moveHelper: ArrayHelpers['move'], columnId: string, atIndex: number) => {
      const currentIndex = findCurrentListColumnIndex(columnId);

      moveHelper(currentIndex, atIndex);
    },
    [findCurrentListColumnIndex],
  );

  const handleRemoveColumnInList = useCallback(
    (removeHelper: ArrayHelpers['remove'], columnIndex: number) => {
      removeHelper(columnIndex);
    },
    [],
  );

  const [, dropConnect] = useDrop(() => ({
    accept: ITEM_TYPES_DND.ROADMAP_COLUM_BOX,
  }));

  return (
    <FieldArray name={name}>
      {({ insert, move, remove }) => (
        <Stack spacing={2} onBlur={() => setFieldTouched(name, true)}>
          <FormHelperText>
            Personaliza la información que se exporta en las documentos de ruta
            según la necesidad de tu empresa.
          </FormHelperText>

          <Stack>
            <Stack
              spacing={2}
              sx={{ width: getInputsMaxWidthPlusIcon(inputsMaxWidth) }}
            >
              <Stack spacing={1} direction="row" alignItems="center">
                <CustomAutocomplete
                  options={options}
                  label="Nueva columna"
                  InputLabelProps={{ shrink: true }}
                  filterSelectedOptions
                  size="small"
                  value={newColumn}
                  onChange={handleChangeNewItem}
                  getOptionDisabled={getAutocompleteOptionDisabled}
                  fullWidth
                  disabled={disabled}
                />

                <Tooltip title="Agregar columna" arrow placement="right">
                  <span>
                    <IconButton
                      aria-label="Add column"
                      onClick={() => handleAddColumInList(insert)}
                      color="primary"
                      size="small"
                      disabled={disabled || !newColumn}
                    >
                      <AddIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>

              {selectedColumnsList.length > 0 && (
                <Stack spacing={2} ref={dropConnect}>
                  {selectedColumnsList.map((columnId, columnIndex) => (
                    <RoadmapColumnsFieldSortableItem
                      key={`roadmap-column-item-sortable-${columnId}`}
                      columnId={columnId}
                      value={getSelectedColumnLabel(columnId)}
                      findColumnIndex={findCurrentListColumnIndex}
                      onMove={(moveColumnId, atIndex) =>
                        handleMoveColumInList(move, moveColumnId, atIndex)
                      }
                      onRemove={() =>
                        handleRemoveColumnInList(remove, columnIndex)
                      }
                      disabled={disabled}
                    />
                  ))}
                </Stack>
              )}
            </Stack>

            {error && (
              <FormHelperText variant="outlined" error>
                {error}
              </FormHelperText>
            )}
          </Stack>
        </Stack>
      )}
    </FieldArray>
  );
};

const WrappedRoadmapColumnsField = (
  props: RoadmapColumnsFieldProps,
): ReactElement => (
  <DndProvider backend={HTML5Backend}>
    <RoadmapColumnsField {...props} />
  </DndProvider>
);

export default WrappedRoadmapColumnsField;
